<template>
  <loading-page
    v-if="initialLoading"
    full
  />
  <div
    v-else
    ref="tableRect"
    class="table-wrapper healthchecks position-relative"
  >
    <div class="d-block d-sm-flex healthchecks-header">
      <div>
        <b-button
          v-if="$can('create', 'healthchecks', 'snitch')"
          variant="bg-primary"
          @click="onCreateHealthcheck()"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create Healthcheck
        </b-button>
      </div>
      <div>
        <b-button
          v-if="$can('create', 'healthchecks', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingHealthcheck"
          class="d-none d-lg-inline action-healthcheck-button"
          @click="onCloneHealthcheck(selected[0].id)"
        >
          Clone
        </b-button>
        <b-button
          v-if="$can('update', 'healthchecks', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingHealthcheck"
          class="d-none d-lg-inline action-healthcheck-button"
          @click="onEditClick(selected[0].id)"
        >
          Edit
        </b-button>
        <button-loading
          v-if="$can('delete', 'healthchecks', 'snitch')"
          variant="bg-white"
          :disabled="!isSelected || removingHealthcheck"
          :spinning="removingHealthcheck"
          class="d-none d-lg-inline action-healthcheck-button"
          @click="onDeleteClick(selected[0].id)"
        >
          Delete
        </button-loading>
        <button-loading
          v-if="$can('update', 'healthchecks', 'snitch') && isSelected"
          variant="bg-white"
          :disabled="!isSelected || removingHealthcheck || updatingHealthcheck"
          :spinning="removingHealthcheck || updatingHealthcheck"
          class="d-none d-lg-inline action-healthcheck-button"
          @click="onToggleClick(selected[0].id, !selected[0].state)"
        >
          {{ selected[0].state ? "Disable" : "Enable" }}
        </button-loading>
      </div>
      <button-loading
        variant="bg-white"
        class="mt-1 mt-sm-0 ml-auto mr-0"
        :spinning="loading"
        :disabled="loading"
        @click="getHealthchecks()"
      >
        <GjIcon
          class="icon-left"
          name="Refresh"
        />
        Refresh
      </button-loading>
    </div>

    <div
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <div class="table-actions">
        <div class="d-flex flex-wrap align-items-center">
          <group-options
            v-model="groupId"
            style="max-width: 255px;"
            class="flex-fill"
          />
          <router-link
            class="link ml-1"
            style="width: 150px;"
            :to="{name: 'healthcheck-groups'}"
          >
            View Groups
            <GjIcon
              name="ArrowRight"
              size="16"
            />
          </router-link>
        </div>
      </div>
      <!-- DESKTOP -->
      <div class="position-relative d-none d-lg-block">
        <b-table
          ref="table"
          :items="items"
          :fields="fields"
          select-mode="single"
          show-empty
          selectable
          responsive
          :class="{ loaded: !loading || removingHealthcheck }"
          :busy="loading"
          @row-selected="onRowSelected"
        >
          <template #cell(selected)="{ rowSelected }">
            <select-button :selected="rowSelected" />
          </template>

          <template #cell(name)="data">
            <a
              v-if="$can('get', 'healthchecks', 'snitch')"
              class="link"
              @click="onDetailsClick(data.item.id)"
            >
              {{ data.value }}
            </a>
            <span v-else>
              {{ data.value }}
            </span>
          </template>

          <template #cell(request)="data">
            <b-badge :variant="changeMethodBadge(data.item.methodToString)">
              {{ data.item.methodToString }}
            </b-badge>
            <a
              v-clipboard:copy="urlFormatted(data.item.protocol, data.item.url)"
              v-clipboard:success="copySuccess"
              class="healthcheck-url"
            >
              <span class="p-0 m-0">
                {{ urlFormatted(data.item.protocol, data.item.url) }}
              </span>
              <span class="copy-url">
                <GjIcon
                  name="Copy"
                  size="17"
                />
              </span>
            </a>
          </template>

          <template #cell(status)="data">
            <status-log
              :state="data.item.state"
              :status="data.item.healthcheckLogStatuses[0]"
              @show-more="showMoreLogs(data.item.healthcheckLogStatuses, $event)"
            />
          </template>

          <template #cell(expectedResponse)="data">
            {{ data.item.expectedResponse || '-' }}:
            {{ data.item.expectedResponseToString || '-' }}
          </template>

          <template #cell(lastChecked)="data">
            {{ data.item.healthcheckLogStatuses[0].executedAt }}
          </template>

          <template #empty>
            <div
              v-if="loading"
              class="loading-table text-center py-1"
            >
              <b-spinner variant="primary" />
            </div>
            <template v-else>
              <no-data
                no-data-text="No healthchecks have been added yet..."
                create-text="+ Create Healthcheck"
                access-page="Healthchecks"
                icon="Monitoring"
                :has-permissions="hasHealthcheckPermissions"
                @action="onCreateHealthcheck()"
              />
            </template>
          </template>
        </b-table>
        <table-busy v-if="loading && items.length > 0" />
      </div>
      <!-- MOBILE -->
      <div class="position-relative d-lg-none mt-2">
        <div :class="{ 'opacity-7': loading || removingHealthcheck }">
          <template v-for="healthcheck in items">
            <card
              :key="healthcheck.id"
              :fields="fields"
              :item="healthcheck"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item
                    v-if="$can('update', 'healthchecks', 'snitch')"
                    @click="onEditClick(healthcheck.id)"
                  >
                    Edit
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'healthchecks', 'snitch')"
                    @click="onDeleteClick(healthcheck.id)"
                  >Delete</b-dropdown-item>
                </b-dropdown>
              </template>

              <template #name>
                <a
                  v-if="$can('get', 'healthchecks', 'snitch')"
                  class="link"
                  @click="onDetailsClick(healthcheck.id)"
                >
                  {{ healthcheck.name }}
                </a>
                <span v-else>
                  {{ healthcheck.name }}
                </span>
              </template>
              <template #status>
                <status-log
                  :state="healthcheck.state"
                  :status="healthcheck.healthcheckLogStatuses[0]"
                  @show-more="showMoreLogs(healthcheck.healthcheckLogStatuses, $event)"
                />
              </template>
              <template #lastChecked>
                <span>
                  {{ healthcheck.healthcheckLogStatuses[0].executedAt || '-' }}
                </span>
              </template>
              <template #request>
                <div>
                  <b-badge :variant="changeMethodBadge(healthcheck.methodToString)">
                    {{ healthcheck.methodToString }}
                  </b-badge>
                  <a
                    v-clipboard:copy="
                      urlFormatted(healthcheck.protocol, healthcheck.url)
                    "
                    v-clipboard:success="copySuccess"
                    class="healthcheck-url"
                  >
                    <span class="p-0 m-0">
                      {{ urlFormatted(healthcheck.protocol, healthcheck.url) }}
                    </span>
                    <span class="copy-url">
                      <GjIcon
                        name="Copy"
                        size="17"
                      />
                    </span>
                  </a>
                </div>
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading || removingHealthcheck"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && items.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No healthchecks have been added yet..."
              create-text="+ Create Healthcheck"
              access-page="Healthchecks"
              icon="Monitoring"
              :has-permissions="hasHealthcheckPermissions"
              @action="onCreateHealthcheck()"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="healthchecks.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <custom-popover
      v-if="showLogList"
      :top="top"
      :left="left"
      class="field-actions p-1"
      @close="showLogList = false"
    >
      <status-log-list :logs="logs" />
    </custom-popover>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'
import CustomPopover from '@/components/popup/CustomPopover.vue'
import GroupOptions from '../components/GroupOptions.vue'
import StatusLog from './components/StatusLog.vue'
import StatusLogList from './components/StatusLogList.vue'

export default {
  name: 'Healthcheck',
  components: {
    Card,
    GroupOptions,
    StatusLog,
    CustomPopover,
    StatusLogList,
  },
  data() {
    return {
      initialLoading: true,
      id: null,
      selected: [],
      items: [],
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'name', isPrimary: true },
        { key: 'request', label: 'Request Method & Path' },
        'expectedResponse',
        {
          key: 'status', isPrimary: true, label: 'Status', variant: 'medium', tdClass: 'py-0',
        },
        'lastChecked',
      ],
      hasHealthcheckPermissions: true,
      showLogList: false,
      tableRect: null,
      rect: null,
      logs: [],
    }
  },
  computed: {
    loading() {
      return this.$store.state.healthchecks.fetchingHealthchecks
    },
    healthchecks() {
      return this.$store.state.healthchecks.healthchecks
    },
    removingHealthcheck() {
      return this.$store.state.healthchecks.removingHealthcheck
    },
    updatingHealthcheck() {
      return this.$store.state.healthchecks.updatingHealthcheck
    },
    isSelected() {
      return this.selected[0]
    },
    totalCount() {
      return this.resultInfo?.totalCount
    },
    resultInfo() {
      return this.$store.state.healthchecks.healthchecksResultInfo
    },
    pageSize() {
      return this.resultInfo?.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentPage: {
      get() {
        return this.$store.getters['healthchecks/currentPage']
      },
      set(value) {
        this.$store.commit('healthchecks/SET_CURRENT_PAGE', value)
      },
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    groupId: {
      get() {
        return this.$store.state.healthchecks.selectedGroup
      },
      set(value) {
        this.$store.commit('healthchecks/SET_SELECTED_GROUP', value)
      },
    },
    top() {
      return this.rect?.top - this.tableRect.top
    },
    left() {
      if (this.currentBreakPoint === 'lg' || this.currentBreakPoint === 'xl') return this.rect.left - this.tableRect.left - 440
      return 0
    },
  },
  watch: {
    currentPage() {
      this.getHealthchecks()
    },
    groupId() {
      if (this.currentPage === 1) {
        this.getHealthchecks()
      } else {
        this.currentPage = 1
      }
    },
  },
  created() {
    this.id = this.$route.params.id
    this.groupId = this.$store.getters['healthchecks/groupId'] || 0
    if (
      this.$store.state.app.previousRouteName !== 'healthchecks-edit'
      && this.$store.state.app.previousRouteName !== 'healthchecks-details'
    ) {
      this.currentPage = 1
    }
    this.getHealthchecks()
  },
  methods: {
    urlFormatted(protocol, url) {
      return protocol === 0 ? `https://${url || '-'}` : `http://${url || '-'}`
    },
    changeMethodBadge(method) {
      if (method === 'GET') {
        return 'light-success'
      }
      if (method === 'DELETE') {
        return 'light-danger'
      }
      if (method === 'POST') {
        return 'light-warning'
      }
      if (method === 'PUT') {
        return 'light-primary'
      }
      return 'light-secondary'
    },
    onRowSelected(item) {
      this.selected = item
    },
    updateList() {
      this.items = this.healthchecks
      this.items.forEach(healthcheck => {
        healthcheck.healthcheckLogStatuses.forEach(e => {
          e.executedAt = e.healthcheckLogId ? `${this.timeSince(e.executedAt)} ago` : '-'
        })
      })
    },
    getHealthchecks() {
      this.$store
        .dispatch('healthchecks/fetchHealthchecks', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          groupId: this.groupId,
        })
        .then(() => {
          this.updateList()
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 426 || status === 428) {
            this.$store.commit('observability/SET_SERVICE_UNAVAILABLE', { serviceUnavailable: true, serviceRequested: status === 428 })
            return
          }
          if (status === 403) {
            this.hasHealthcheckPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthchecks!',
          )
        })
        .finally(() => { this.initialLoading = false })
    },
    onEditClick(healthcheckId) {
      this.$router.push({
        name: 'healthchecks-edit',
        params: { id: this.id, healthcheckId },
      })
    },
    onDetailsClick(healthcheckId) {
      this.$router.push({
        name: 'healthchecks-details',
        params: { id: this.id, healthcheckId },
      })
    },
    onCreateHealthcheck() {
      this.$router.push({
        name: 'healthchecks-create',
        params: { id: this.id },
      })
    },
    onCloneHealthcheck(healthcheckId) {
      this.$router.push({
        name: 'healthchecks-clone',
        params: { id: this.id, healthcheckId },
      })
    },
    onDeleteClick(healthcheckId) {
      this.confirmDelete(
        'Delete Healthcheck',
        'Are you sure you want to delete this healthcheck?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('healthchecks/removeHealthcheck', healthcheckId)
            .then(() => {
              this.successToast('Healthcheck was removed')
              this.selected = []
              if (this.healthchecks.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.getHealthchecks()
              }
            })
            .catch(error => {
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting healthcheck.')
            })
        }
      })
    },
    onToggleClick(id, state) {
      this.$store
        .dispatch('healthchecks/toggleHealthcheck', { id, state })
        .then(() => {
          this.getHealthchecks()
        })
        .catch(error => {
          const { status } = error.response || null
          if (status === 426 || status === 428) {
            this.$store.commit('observability/SET_SERVICE_UNAVAILABLE', { serviceUnavailable: true, serviceRequested: status === 428 })
            return
          }
          if (status === 403) {
            this.hasHealthcheckPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching healthchecks!',
          )
        })
        .finally(() => { this.initialLoading = false })
    },
    showMoreLogs(logs, e) {
      this.tableRect = this.$refs.tableRect.getBoundingClientRect()
      this.rect = e.target.getBoundingClientRect()
      this.logs = logs
      this.showLogList = true
      e.stopPropagation()
    },
  },
}
</script>
<style lang="scss" scoped>
.healthchecks {
  .healthchecks-header {
    flex-wrap: wrap;
    margin-left: auto;
    .action-healthcheck-button {
      background-color: #fff;
    }
  }
  .badge {
    ::v-deep {
      font-size: 80%;
    }
  }
  .healthcheck-url {
    color: #1d79f2;
    &:hover {
      color: #0d6ce8;
    }
  }
}

.field-actions {
  width: 440px;
  max-width: calc(100% - 20px);
}
</style>
